import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import {
  LayoutDashboard, ListChecks, MessageSquare, ShieldAlert, Ban,
  Archive, BarChart3, Sparkles, Settings, LogOut, Menu, X, Mail, Inbox, ShieldCheck, Bell, AlertTriangle, FileText,
} from "lucide-react";
import SeoHead from "@/components/SeoHead";
import { adminApi, fetchMe, logout as apiLogout } from "@/lib/adminApi";

const NAV = [
  { to: "/admin/dashboard", label: "Dashboard", Icon: LayoutDashboard },
  { to: "/admin/active-questions", label: "Active Questions", Icon: ListChecks },
  { to: "/admin/messages", label: "Messages", Icon: MessageSquare },
  { to: "/admin/moderation", label: "Moderation", Icon: ShieldAlert, alertKey: "messages_flagged" },
  { to: "/admin/spam", label: "Spam", Icon: Ban },
  { to: "/admin/archives", label: "Archives", Icon: Archive },
  { to: "/admin/analytics", label: "Analytics", Icon: BarChart3 },
  { to: "/admin/reports", label: "Reports", Icon: FileText },
  { to: "/admin/content-studio", label: "Content Studio", Icon: Sparkles },
  { to: "/admin/newsletter", label: "Newsletter", Icon: Mail },
  { to: "/admin/contact", label: "Contact", Icon: Inbox, alertKey: "contact_unread" },
  { to: "/admin/login-attempts", label: "Login Attempts", Icon: ShieldCheck, alertKey: "denied_logins_24h" },
  { to: "/admin/settings", label: "Settings", Icon: Settings },
];

const SEVERITY_STYLE = {
  critical: { bg: "#2a0a0a", border: "#E3000F", ink: "#FFB1B1", dot: "#E3000F" },
  warning: { bg: "#2a1a0a", border: "#FF9933", ink: "#FFD09b", dot: "#FF9933" },
  info: { bg: "#0a1a2a", border: "#3a82f6", ink: "#9bc6ff", dot: "#3a82f6" },
};

export const AdminLayout = () => {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(false);
  const [alerts, setAlerts] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [reconciling, setReconciling] = useState(false);
  const [reconciledMsg, setReconciledMsg] = useState(null);

  const reconcileFailed = async () => {
    if (reconciling) return;
    if (!window.confirm(
      "Zero out the recorded cost on any historical FAILED LLM calls?\n\n" +
      "This only affects audit rows where success=false. Successful generations are untouched. " +
      "Safe to run multiple times — it just won't find any more rows once they're already reconciled."
    )) return;
    setReconciling(true); setReconciledMsg(null);
    try {
      const r = await adminApi.post("/admin/llm-usage/reconcile-failed");
      const inr = (r.data?.total_refunded_inr || 0).toFixed(2);
      const n = r.data?.updated || 0;
      setReconciledMsg(
        n > 0
          ? `Reconciled ${n} failed call${n === 1 ? "" : "s"} · ~₹${inr} removed from the running total.`
          : "Nothing to reconcile — no historical failed calls with a non-zero cost."
      );
      // Refresh alerts so the spend strip updates
      try {
        const a = await adminApi.get("/admin/alerts");
        setAlerts(a.data);
      } catch { /* ignore */ }
    } catch (e) {
      setReconciledMsg(`Failed: ${e?.response?.data?.detail || e?.message || "unknown error"}`);
    } finally {
      setReconciling(false);
      setTimeout(() => setReconciledMsg(null), 8000);
    }
  };

  // Poll alerts every 60s
  useEffect(() => {
    if (!me) return undefined;
    let cancelled = false;
    const load = () => {
      adminApi.get("/admin/alerts")
        .then((r) => { if (!cancelled) setAlerts(r.data); })
        .catch(() => { /* silent */ });
    };
    load();
    const id = setInterval(load, 60_000);
    return () => { cancelled = true; clearInterval(id); };
  }, [me]);

  useEffect(() => {
    fetchMe()
      .then(setMe)
      .catch((e) => {
        const code = e?.response?.status;
        // Any auth failure (no cookie, expired session, network blip) → push to login.
        // This is safer than showing a dead-end error screen, especially in production
        // where a missing cookie should never look like a 500.
        if (!code || code === 401 || code === 403) {
          navigate("/admin/login", { replace: true });
        } else {
          setError(e?.response?.data?.detail || `Auth check failed (HTTP ${code}).`);
        }
      });
  }, [navigate]);

  // Update browser tab title with a notification count for critical alerts
  useEffect(() => {
    if (!alerts || typeof document === "undefined") return;
    const criticalCount = (alerts.alerts || []).filter((a) => a.severity === "critical").length;
    const baseTitle = document.title.replace(/^\(\d+\)\s*/, "");
    document.title = criticalCount > 0 ? `(${criticalCount}) ${baseTitle}` : baseTitle;
  }, [alerts]);

  const handleLogout = async () => {
    try { await apiLogout(); } catch { /* ignore */ }
    navigate("/admin/login", { replace: true });
  };

  if (!me && !error) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#0A0A0A", color: "#FF9933" }} data-testid="admin-bootstrap">
        <p className="font-display uppercase text-xl tracking-[0.3em]">Checking session…</p>
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center px-5" style={{ background: "#0A0A0A", color: "#FDFBF7" }}>
        <div className="border-2 border-[#E3000F] p-6 max-w-md w-full" style={{ background: "#141414" }}>
          <p className="text-xs uppercase font-bold tracking-[0.3em]" style={{ color: "#E3000F" }}>Error</p>
          <p className="mt-2 text-sm">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex" style={{ background: "#0A0A0A", color: "#FDFBF7" }} data-testid="admin-shell">
      <SeoHead title="Kerala Speaks Admin" description="Admin console — Kerala Speaks." noindex />
      {/* Sidebar */}
      <aside
        className={`${open ? "translate-x-0" : "-translate-x-full"} md:translate-x-0 fixed md:sticky top-0 left-0 z-40 h-screen w-64 transition-transform border-r-2 border-[#1f1f1f] flex flex-col`}
        style={{ background: "#0F0F0F" }}
        data-testid="admin-sidebar"
      >
        <div className="px-5 py-5 border-b-2 border-[#1f1f1f]">
          <p className="text-[10px] uppercase font-bold tracking-[0.4em]" style={{ color: "#FF9933" }}>★ KERALA SPEAKS ★</p>
          <p className="font-display uppercase text-lg mt-1 leading-none">ADMIN CMS</p>
        </div>
        <nav className="flex-1 py-3 overflow-y-auto">
          {NAV.map(({ to, label, Icon, alertKey }) => {
            const count = alertKey && alerts ? (alerts[alertKey] || 0) : 0;
            const isSecurity = alertKey === "denied_logins_24h";
            const badgeColor = isSecurity && count >= 5 ? "#E3000F" : "#FF9933";
            return (
              <NavLink
                key={to}
                to={to}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-5 py-3 text-xs uppercase font-extrabold tracking-[0.25em] border-l-4 transition-colors ${
                    isActive
                      ? "border-[#FF9933] bg-[#1a1a1a] text-[#FF9933]"
                      : "border-transparent text-neutral-400 hover:text-[#FDFBF7] hover:bg-[#141414]"
                  }`
                }
                data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <Icon size={16} strokeWidth={3} />
                <span className="flex-1">{label}</span>
                {count > 0 && (
                  <span
                    className="inline-flex items-center justify-center min-w-[20px] h-[20px] px-1.5 text-[10px] font-extrabold rounded-full"
                    style={{ background: badgeColor, color: "#0A0A0A", lineHeight: 1 }}
                    data-testid={`nav-badge-${label.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    {count > 99 ? "99+" : count}
                  </span>
                )}
              </NavLink>
            );
          })}
        </nav>
        <div className="border-t-2 border-[#1f1f1f] p-4">
          <div className="flex items-center gap-3 mb-3">
            {me?.picture ? (
              <img src={me.picture} alt="" className="w-9 h-9 border-2 border-[#1f1f1f] object-cover" />
            ) : (
              <div className="w-9 h-9 border-2 border-[#1f1f1f] bg-[#1a1a1a] flex items-center justify-center text-[10px] font-extrabold">
                {(me?.name || me?.email || "A").slice(0, 1).toUpperCase()}
              </div>
            )}
            <div className="min-w-0">
              <p className="text-xs font-bold truncate" data-testid="admin-me-name">{me?.name || me?.email}</p>
              <p className="text-[10px] uppercase font-bold tracking-[0.25em] text-neutral-500 truncate" title={me?.email} data-testid="admin-me-email">{me?.email}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-center gap-2 border-2 border-[#1f1f1f] hover:border-[#E3000F] hover:text-[#E3000F] py-2 text-[10px] uppercase font-extrabold tracking-[0.3em] transition-colors"
            data-testid="admin-logout"
          >
            <LogOut size={12} strokeWidth={3} /> Sign out
          </button>
        </div>
      </aside>

      {open && (
        <div className="fixed inset-0 bg-black/60 z-30 md:hidden" onClick={() => setOpen(false)} />
      )}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="md:hidden flex items-center justify-between px-4 py-3 border-b-2 border-[#1f1f1f]" style={{ background: "#0F0F0F" }}>
          <p className="font-display uppercase text-base">Kerala Speaks Admin</p>
          <button onClick={() => setOpen((s) => !s)} aria-label="menu" data-testid="admin-mobile-menu">
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </header>
        <main className="flex-1 p-5 md:p-8 max-w-6xl w-full">
          {alerts?.llm_spend && (
            <div
              className="mb-4 border-2 px-3 py-2 flex flex-wrap items-center justify-between gap-2"
              style={{ borderColor: "#1f1f1f", background: "rgba(255,153,51,0.05)" }}
              data-testid="admin-llm-spend"
              title={`Estimated based on logged Claude calls × Anthropic published rates, converted at ~₹83 / USD. Source of truth: Profile → Universal Key.`}
            >
              <p className="text-[10px] uppercase font-extrabold tracking-[0.3em]" style={{ color: "#FF9933" }}>
                LLM spend · This month
                <span className="ml-2" style={{ color: "#FDFBF7" }} data-testid="llm-spend-inr">
                  ~₹{((alerts.llm_spend.month_to_date_usd || 0) * 83).toFixed(2)}
                </span>
              </p>
              <p className="text-[10px] uppercase font-bold tracking-[0.25em] text-neutral-500" data-testid="llm-spend-breakdown">
                {Object.entries(alerts.llm_spend.by_kind || {})
                  .map(([k, v]) => `${k} ${v.calls}`).join(" · ") || "no calls yet"}
                {" · "}
                <span className="text-neutral-600">Estimate at ₹83/USD. See Profile → Universal Key for actual.</span>
              </p>
              <button
                onClick={reconcileFailed}
                disabled={reconciling}
                data-testid="llm-spend-reconcile"
                className="text-[10px] uppercase font-extrabold tracking-[0.25em] border-2 px-2 py-1 hover:opacity-90 disabled:opacity-50"
                style={{ borderColor: "#FF9933", color: "#FF9933", background: "transparent" }}
                title="Zero out cost on historical failed LLM calls (one-time cleanup)."
              >
                {reconciling ? "Reconciling…" : "Reconcile failed"}
              </button>
            </div>
          )}
          {reconciledMsg && (
            <div
              className="mb-4 border-2 px-3 py-2 text-[11px] uppercase font-bold tracking-[0.2em]"
              style={{ borderColor: "#FF9933", background: "rgba(255,153,51,0.08)", color: "#FDFBF7" }}
              data-testid="llm-reconcile-result"
            >
              {reconciledMsg}
            </div>
          )}
          {!dismissed && alerts?.alerts?.length > 0 && (
            <div className="mb-5 space-y-2" data-testid="admin-alerts">
              <div className="flex items-center justify-between gap-3 mb-1">
                <p className="text-[10px] uppercase font-extrabold tracking-[0.35em]" style={{ color: "#FF9933" }}>
                  <Bell size={11} strokeWidth={3} className="inline mr-1.5 -mt-0.5" />
                  Active alerts ({alerts.alerts.length})
                </p>
                <button
                  type="button"
                  onClick={() => setDismissed(true)}
                  className="text-[10px] uppercase font-bold tracking-[0.3em] text-neutral-500 hover:text-neutral-300"
                  data-testid="admin-alerts-dismiss"
                >
                  Dismiss ×
                </button>
              </div>
              {alerts.alerts.map((a, i) => {
                const style = SEVERITY_STYLE[a.severity] || SEVERITY_STYLE.info;
                const Icon = a.severity === "critical" ? AlertTriangle : Bell;
                return (
                  <Link
                    key={`${a.type}-${i}`}
                    to={a.link}
                    className="flex items-start gap-3 border-2 p-3 hover:opacity-90 transition-opacity"
                    style={{ background: style.bg, borderColor: style.border, color: style.ink }}
                    data-testid={`admin-alert-${a.type}`}
                  >
                    <Icon size={16} strokeWidth={3} className="shrink-0 mt-0.5" style={{ color: style.dot }} />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs uppercase font-extrabold tracking-[0.25em]">{a.message}</p>
                      <p className="text-[10px] uppercase font-bold tracking-[0.3em] opacity-70 mt-0.5">
                        {a.severity} · Click to investigate →
                      </p>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
          <Outlet context={{ me, alerts }} />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
